import type { AdGroupSummary } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { type ReportingPeriod, resolveReportingPeriod } from "@/stores/reporting-period";
import { CampaignAdGroupsTable } from "./CampaignAdGroupsTable";
import { campaignAdGroupsQuery, precedingAdGroupsPeriod } from "./campaign-ad-groups-query";

interface CampaignAdGroupsProps {
  campaignId: string;
  range: ReportingPeriod;
  timeZone: ReportingTimeZone;
  children?: ReactNode;
  renderAdGroupLink?: (group: AdGroupSummary) => ReactNode;
}
export function CampaignAdGroups({
  campaignId,
  range,
  timeZone,
  children,
  renderAdGroupLink,
}: CampaignAdGroupsProps) {
  const [comparison, setComparison] = useState(false);
  const dates = resolveReportingPeriod(range, new Date(), timeZone);
  const query = useQuery(campaignAdGroupsQuery(campaignId, { type: "custom", ...dates }, timeZone));
  const previous = useQuery({
    ...campaignAdGroupsQuery(campaignId, precedingAdGroupsPeriod(dates), timeZone),
    enabled: comparison,
  });
  return (
    <CampaignAdGroupsTable
      campaignId={campaignId}
      data={query.data}
      previousData={comparison ? previous.data : undefined}
      isLoading={query.isPending}
      isFetching={query.isFetching}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      comparison={comparison}
      onComparisonChange={setComparison}
      comparisonLoading={comparison && previous.isPending}
      comparisonError={comparison && previous.isError}
      onComparisonRetry={() => void previous.refetch()}
      renderAdGroupLink={renderAdGroupLink}
    >
      {children}
    </CampaignAdGroupsTable>
  );
}
